/**
 * The model's findings, put back into the text.
 *
 * They were stored as exercises and never shown where they happened. That is
 * half the point missed: a mistake one sees in one's own sentence is a
 * mistake one recognises, and the exercise a week later is the second half,
 * not the first.
 *
 * It matters most for what the spell checker cannot see. „den" instead of
 * „denn", „wiedersthet" instead of „widersteht", „dir" instead of „die" —
 * every one of them is a real word, so LanguageTool walks past. Those are
 * exactly the ones the model is asked about, and until now its answer went
 * straight into the exercise rotation without ever being shown.
 *
 * Finding the place again is the whole job here. The stored mistake knows
 * the word and the sentence it stood in, not an offset — and by the time
 * this runs the text may have been written on. So: find the sentence, find
 * the word in it, and only mark what is beyond doubt.
 */

import type { Finding, Mistake } from '../types.js'

/** Word characters, as everywhere else in the app. */
const WORD = /[\p{L}\p{N}]/u

/** Does a whole word stand at this place — not a piece of a longer one? */
function standsAlone(text: string, at: number, len: number): boolean {
  const before = at > 0 ? text[at - 1] : ''
  const after = text[at + len] ?? ''
  return !WORD.test(before || ' ') && !WORD.test(after || ' ')
}

/**
 * Every place where `word` stands as a word of its own.
 */
function placesOf(text: string, word: string): number[] {
  const out: number[] = []
  if (!word) return out
  let i = text.indexOf(word)
  while (i !== -1) {
    if (standsAlone(text, i, word.length)) out.push(i)
    i = text.indexOf(word, i + 1)
  }
  return out
}

/**
 * The stored findings of the model, as findings in THIS text.
 *
 * Only the unambiguous ones. A word that stands three times in the text and
 * whose sentence is no longer there could be marked in the wrong place, and
 * a mark on a correct word is worse than a missing one — it teaches that
 * something right is wrong.
 *
 * @param text      what stands on the paper now
 * @param mistakes  what the model found, from the store
 * @param solved    keys of mistakes already practised away
 */
export function modelFindings(
  text: string,
  mistakes: readonly Mistake[],
  solved: ReadonlySet<string> = new Set()
): Finding[] {
  const out: Finding[] = []
  const taken: { start: number; end: number }[] = []

  for (const m of mistakes) {
    if (m.solved || solved.has(m.id)) continue
    if (!m.wrong || !m.right || m.wrong === m.right) continue

    const places = placesOf(text, m.wrong)
    if (!places.length) continue

    let at: number | undefined
    if (places.length === 1) {
      at = places[0]
    } else if (m.context) {
      // Several places: the sentence decides. Its position in the text plus
      // the position of the word inside it gives one place — and if the
      // sentence is gone, nothing is marked.
      const s = text.indexOf(m.context)
      if (s !== -1) {
        const inSentence = placesOf(m.context, m.wrong)
        const first = inSentence[0]
        if (inSentence.length === 1 && first != null) at = s + first
      }
    }
    if (at == null) continue

    // Never twice over the same spot: the spell checker may have found it
    // too, and two marks on one word are one mark too many.
    const end = at + m.wrong.length
    if (taken.some((t) => at < t.end && end > t.start)) continue
    taken.push({ start: at, end })

    out.push({
      word: m.wrong,
      start: at,
      end,
      replacements: [m.right],
      // Its own rule name, so the grouping in the list can tell them apart
      // and the help card knows it is a whole word being replaced.
      rule: `MODELL_${m.kind.toUpperCase().replace(/-/g, '_')}`,
      source: 'model',
    })
  }

  return out
}

/**
 * Drops the model findings that the spell checker already covers.
 *
 * Both mark the same word only in the rarer case; when they do, the spell
 * checker's finding wins, because it knows the offset from the text itself
 * rather than from a search.
 */
export function withoutOverlap(
  model: readonly Finding[],
  checker: readonly Finding[]
): Finding[] {
  return model.filter(
    (m) => !checker.some((c) => m.start < c.end && m.end > c.start)
  )
}
